import React, { useEffect, useState } from "react";
import { getCreditAnalysisProductLevelInformation } from "../../../../shared/services/apis/tenantAnalytics";
import IframeViewer from "../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  Packer,
  convertInchesToTwip,
  TableLayoutType,
  PageOrientation,
  Footer,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import { getTenantById } from "../../../../shared/services/apis/tenant";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

interface ProductLevelInformationProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export const ProductLevelInformation: React.FC<
  ProductLevelInformationProps
> = ({ tenantId, tenantServiceId, tenantServiceOptionId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCreditAnalysisProductLevelInformation(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
        setError(null);
      } catch {
        setError("Failed to load product information");
      } finally {
        setLoading(false);
      }
    };
    if (tenantId && tenantServiceId && tenantServiceOptionId) fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const downloadPDF = async () => {
    setDownloading(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const product = data?.content?.facility_product_level_information;

      // Load the watermark image with proper typing
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);

        // Save graphics state and set transparency
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword

        // Calculate watermark size and position (centered)
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        // Add watermark image
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        // Restore graphics state
        doc.restoreGraphicsState();
      };
      // Header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Product-Level Information", pageWidth / 2, 20, {
        align: "center",
      });

      // Date and time
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
        align: "center",
      });

      let yPosition = 40;

      // Facility Definitions Section
      if (product?.definitions) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Facility Definitions", 14, yPosition);
        yPosition += 8;

        const definitionsData = Object.entries(product.definitions)
          .filter(([_, value]) => typeof value === "string")
          .map(([key, value]) => [
            key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase()),
            value as string,
          ]);

        autoTable(doc, {
          startY: yPosition,
          head: [["Facility Type", "Definition"]],
          body: definitionsData,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: {
            fillColor: [243, 244, 246],
            textColor: [55, 65, 81],
            fontStyle: "bold",
          },
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: "auto" },
          },
          margin: { left: 14, right: 14 },
        });

        yPosition = (doc as any).lastAutoTable.finalY + 10;
      }

      // Business Overview Section
      if (data?.content?.basic_company_facts?.business_overview) {
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Company Business Overview", 14, yPosition);
        yPosition += 8;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const splitText = doc.splitTextToSize(
          data.content.basic_company_facts.business_overview,
          pageWidth - 28
        );
        doc.text(splitText, 14, yPosition);
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      const fileName = `Product_Level_Information_${new Date().getTime()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);

    try {
      // Helper function to convert base64 to Uint8Array
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      const product = data?.content?.facility_product_level_information;
      const children: (Paragraph | Table)[] = [];

      const pageWidth = convertInchesToTwip(8.5);
      const marginLeft = convertInchesToTwip(0.75);
      const marginRight = convertInchesToTwip(0.75);
      const availableWidth = pageWidth - marginLeft - marginRight;

      // Header
      children.push(
        new Paragraph({
          text: "Product-Level Information",
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200, before: 200 },
        })
      );

      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated on ${currentDateTime}`,
              size: 20,
              color: "666666",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 },
        })
      );

      // Facility Definitions
      if (product?.definitions) {
        children.push(
          new Paragraph({
            text: "Facility Definitions",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        const col1Width = availableWidth * 0.3;
        const col2Width = availableWidth * 0.7;

        const definitionRows = [
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Facility Type",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col1Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Definition",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col2Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
            ],
            tableHeader: true,
          }),
        ];

        const definitions = Object.entries(product.definitions).filter(
          ([_, value]) => typeof value === "string"
        );

        definitions.forEach(([key, value], idx) => {
          const label = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l: string) => l.toUpperCase());

          definitionRows.push(
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      text: label,
                      spacing: { after: 80, before: 80 },
                    }),
                  ],
                  shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                  width: { size: col1Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      text: value as string,
                      spacing: { after: 80, before: 80 },
                    }),
                  ],
                  shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                  width: { size: col2Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
              ],
            })
          );
        });

        const definitionsTable = new Table({
          rows: definitionRows,
          width: { size: availableWidth, type: WidthType.DXA },
          columnWidths: [col1Width, col2Width],
          layout: TableLayoutType.FIXED,
          borders: {
            top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            insideHorizontal: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
            insideVertical: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
          },
        });

        children.push(definitionsTable);
        children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
      }

      // Business Overview
      if (data?.content?.basic_company_facts?.business_overview) {
        children.push(
          new Paragraph({
            text: "Company Business Overview",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        children.push(
          new Paragraph({
            text: data.content.basic_company_facts.business_overview,
            spacing: { after: 200, before: 200 },
            alignment: AlignmentType.JUSTIFIED,
          })
        );
      }

      // Create footer with logo in right bottom corner
      const footerChildren: Paragraph[] = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      // Create Document
      const doc = new Document({
        creator: "Product Analysis System",
        title: "Product-Level Information",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(0.75),
                  left: convertInchesToTwip(0.75),
                  right: convertInchesToTwip(0.75),
                },
                size: {
                  width: convertInchesToTwip(8.5),
                  height: convertInchesToTwip(11),
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `Product_Level_Information_${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading)
    return <div className="h-64 bg-gray-200 rounded animate-pulse" />;

  if (error)
    return (
      <div className="max-w-4xl mx-auto mt-8">
        <div className="bg-gray-50 border border-gray-300 rounded-xl p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Data
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );

  const product = data?.content?.facility_product_level_information;

  return (
    <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 max-w-4xl mx-auto">
      {/* Header with Download Buttons */}
      <div className="pb-6 border-b border-gray-300 mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl text-gray-700 font-light">
            Product-Level Information
          </h3>

          <div className="flex gap-2">
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  PDF
                </>
              )}
            </button>

            <button
              onClick={downloadWord}
              disabled={downloadingWord}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingWord ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  Word
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Facility Definitions */}
      {product?.definitions && (
        <div className="mb-6 pb-6 border-b border-gray-300">
          <h4 className="text-md font-light text-gray-500 mb-4">
            Facility Definitions
          </h4>
          <div className="overflow-x-auto rounded-lg border border-gray-300">
            <table className="min-w-full  rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm">
                    Facility Type
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm">
                    Definition
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(product.definitions).map(
                  ([key, value]) =>
                    typeof value === "string" && (
                      <tr
                        key={key}
                        className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 px-4 text-sm font-medium text-gray-800 capitalize align-top">
                          {key.replace(/_/g, " ")}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700 leading-relaxed">
                          {value}
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Business Overview */}
      {data?.content?.basic_company_facts && (
        <div className="mb-6 pb-6 border-b border-gray-300">
          <h4 className="text-md font-light text-gray-500 mb-4">
            Company Business Overview
          </h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            {data.content.basic_company_facts.business_overview}
          </p>
        </div>
      )}

      {/* Citations */}
      {data?.citations && data.citations.length > 0 && (
        <div className="text-gray-700 text-sm">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Sources</h4>
          <p className="mb-1">
            {data.citations.map((sourceUrl: string, i: number) => {
              const url = new URL(sourceUrl);
              const display = url.hostname.replace("www.", "");
              return (
                <button
                  key={i}
                  onClick={(e) => handleSourceClick(e, sourceUrl)}
                  className="text-xs text-gray-700 border ml-1 mb-1 border-gray-300 font-medium px-2 py-1 rounded-full hover:bg-gray-50 cursor-pointer"
                >
                  {display}
                </button>
              );
            })}
          </p>
        </div>
      )}

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
